// components/ConversationSidebar.js
import { useState } from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import ReactMarkdown from 'react-markdown'

import './ConversationSideBar.css'

function ConversationSidebar ({
  isPermanent = false,
  isCollapsed = false,
  onToggleCollapse,
  onNewChat,
  isOpen = false,
  onClose,
  conversations,
  currentConversation,
  loading,
  onLoadConversation,
  onArchiveConversation,
  onDeleteConversation,
  onUpdateTitle
}) {

  const formatDate = date => {
    if (!date) return 'Unbekannt'
    const d = date instanceof Date ? date : new Date(date)
    const now = new Date()
    const diffMs = now - d
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Gerade eben'
    if (diffMins < 60) return `vor ${diffMins}m`
    if (diffHours < 24) return `vor ${diffHours}h`
    if (diffDays < 7) return `vor ${diffDays}d`

    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short'
    })
  }

  const getConversationPreview = conversation => {
    if (conversation.lastMessage) {
      const previewText =
        conversation.lastMessage.content.substring(0, 60) +
        (conversation.lastMessage.content.length > 60 ? '...' : '')

      return (
        <div className='conversation-preview'>
          <ReactMarkdown
            components={{
              p: ({ children }) => (
                <span className='preview-text'>{children}</span>
              ),
              strong: ({ children }) => (
                <strong className='preview-strong'>{children}</strong>
              ),
              em: ({ children }) => <em className='preview-em'>{children}</em>,
              code: ({ children }) => (
                <code className='preview-code'>{children}</code>
              )
            }}
          >
            {previewText}
          </ReactMarkdown>
        </div>
      )
    }
    return <span className='no-message'>Keine Nachrichten</span>
  }

  const renderConversationItem = conversation => {
    const isActive = currentConversation?.id === conversation.id

    if (isCollapsed) {
      return (
        <div
          key={conversation.id}
          className={`conversation-item collapsed ${isActive ? 'active' : ''}`}
          onClick={() => onLoadConversation(conversation.id)}
          title={conversation.title}
        >
          <div className='conversation-icon'>
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <path d='M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z' />
            </svg>
          </div>
          {conversation.unreadCount > 0 && (
            <span className='unread-badge'>{conversation.unreadCount}</span>
          )}
        </div>
      )
    }

    return (
      <div
        key={conversation.id}
        className={`conversation-item ${isActive ? 'active' : ''}`}
        onClick={() => onLoadConversation(conversation.id)}
      >
        <button
          className='delete-btn'
          onClick={e => {
            e.stopPropagation()
            if (window.confirm('Conversation wirklich löschen?')) {
              onDeleteConversation(conversation.id)
            }
          }}
          title='Löschen'
        >
          <DeleteIcon fontSize='small' />
        </button>
        <div className='conversation-content'>
          <div className='conversation-header'>
            <div className='icon-and-title'>
              <div className='conversation-icon'>
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                >
                  <path d='M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z' />
                </svg>
              </div>
              <div className='title-section'>
                <h4 className='conversation-title' title={conversation.title}>
                  {conversation.title}
                </h4>
              </div>
            </div>
          </div>

          <div className='conversation-meta'>
            <span className='date'>
              {formatDate(conversation.lastActivity)}
            </span>
            <span className='separator'>•</span>
            <span className='message-count'>
              {conversation.messageCount || 0} Nachrichten
            </span>
          </div>

          {conversation.lastMessage && (
            <div className='conversation-preview'>
              {getConversationPreview(conversation)}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`conversation-sidebar ${isPermanent ? 'permanent' : ''} ${
        isCollapsed ? 'collapsed' : ''
      } ${isOpen ? 'open' : ''}`}
    >
      {!isPermanent && (
        <div className='sidebar-overlay' onClick={onClose}></div>
      )}

      <div className='sidebar-content'>
        <div className='sidebar-header'>
          {!isCollapsed && <h3>Gespräche</h3>}
          <button
            className='toggle-btn'
            onClick={isPermanent ? onToggleCollapse : onClose}
            title={isCollapsed ? 'Erweitern' : 'Minimieren'}
          >
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              {isCollapsed ? (
                <path d='M9 18l6-6-6-6' />
              ) : (
                <path d='M15 18l-6-6 6-6' />
              )}
            </svg>
          </button>
        </div>

        <div className='sidebar-body'>
          {!isCollapsed && onNewChat && (
            <button className='new-chat-btn' onClick={onNewChat}>
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <path d='M12 5v14m-7-7h14' />
              </svg>
              <span>Neues Gespräch</span>
            </button>
          )}

          {isCollapsed && onNewChat && (
            <button
              className='new-chat-btn icon-only'
              onClick={onNewChat}
              title='Neues Gespräch'
            >
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <path d='M12 5v14m-7-7h14' />
              </svg>
            </button>
          )}

          {loading && (
            <div className='loading-state'>
              <div className='spinner'></div>
              {!isCollapsed && <span>Lädt...</span>}
            </div>
          )}

          {!loading && conversations.length === 0 && (
            <div className='empty-state'>
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.5'
              >
                <path d='M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z' />
              </svg>
              {!isCollapsed && (
                <>
                  <h4>Keine Gespräche</h4>
                  <p>Starte ein neues Gespräch</p>
                </>
              )}
            </div>
          )}

          {!loading && conversations.length > 0 && (
            <div className='conversations-list'>
              {conversations.map(conversation =>
                renderConversationItem(conversation)
              )}
            </div>
          )}
        </div>

        {!isCollapsed && (
          <div className='sidebar-footer'>
            <span className='conversation-count'>
              {conversations.length}{' '}
              {conversations.length === 1 ? 'Gespräch' : 'Gespräche'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConversationSidebar
